import { useCallback } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useSettings } from "../useSetting";
import { ISetting } from "@/domain/setting.interface";
import { displayDateOfBirth } from "../../utils/temporal.utils";
import { formatAccountDate } from "../../utils/accountDate";

dayjs.extend(utc);
dayjs.extend(timezone);

type DateFormat = "MM/DD/YYYY" | "DD/MM/YYYY" | "YYYY-MM-DD";

/**
 * Hook for date formatting based on the countryFormat account setting.
 * Uses Temporal.PlainDate under the hood to avoid timezone offset issues.
 * Time-bearing values are rendered in the account's IANATimeZone setting.
 */
export const useDateFormat = () => {
	const { data: settings } = useSettings();
	const { countryFormat, IANATimeZone } = settings || ({} as ISetting);
	const dateFormat =
		((countryFormat as { dateFormat?: string })?.dateFormat as DateFormat) ||
		"MM/DD/YYYY";
	const tz = (IANATimeZone as string | undefined) || undefined;

	/**
	 * Format a date of birth for display.
	 * Handles both legacy ISO dates ("1993-07-02T06:00:00.000Z")
	 * and already-formatted dates ("07/02/1993").
	 */
	const formatDob = useCallback(
		(dateString: string | null | undefined): string =>
			displayDateOfBirth(dateString, dateFormat) || "-",
		[dateFormat],
	);

	/**
	 * Date-only formatter — does NOT apply IANATimeZone.
	 * Use for values that represent a plain calendar date (no time-of-day semantics),
	 * e.g. enrollment date, report date. For instants in time, use formatDateTime.
	 */
	const formatDate = useCallback(
		(value: string | Date | null | undefined): string =>
			value ? dayjs(value).format(dateFormat) : "-",
		[dateFormat],
	);

	/**
	 * Instant → calendar date in the ACCOUNT's timezone (SIMS-9014 / SIMS-9013).
	 *
	 * Use for a value that is a point in time — a payout, a visit — where the
	 * question "which day was that?" has to be answered the same way the server
	 * answers it when filtering. `formatDate` above renders in the BROWSER's
	 * zone, so on a machine west of the account a midnight-local timestamp draws
	 * a day early and the report's own date filter then cannot find the row by
	 * the date it just displayed. See `utils/accountDate.ts` for the full story.
	 *
	 * `formatDate` is deliberately left alone: it is correct for plain calendar
	 * dates, which carry no time of day and must not be shifted at all.
	 */
	const formatInstantDate = useCallback(
		(value: string | Date | null | undefined): string =>
			formatAccountDate(value, dateFormat, tz),
		[dateFormat, tz],
	);

	const formatDateTime = useCallback(
		(value: string | Date | null | undefined): string => {
			if (!value) return "—";
			const d = dayjs(value);
			return (tz ? d.tz(tz) : d).format(`${dateFormat} hh:mm A`);
		},
		[dateFormat, tz],
	);

	return {
		dateFormat,
		// The account's IANA zone, for callers that must resolve a date outside
		// React — the Excel builder, which needs a date VALUE rather than text.
		timeZone: tz,
		formatDob,
		formatDate,
		formatInstantDate,
		formatDateTime,
	};
};
